import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { BoardColumnKey } from '../../utils/job-tracking/pipeline-stage-meta';
import type { JobSortKey } from '../../utils/job-tracking/sort-jobs';

/** Search/company/sort/hide-inactive controls shared by the desktop kanban and mobile board on /tools, so a filter set on one surface is still applied after navigating away and back. */
interface KanbanFiltersState {
  query: string;
  companyFilter: string;
  sortBy: JobSortKey;
  hideInactive: boolean;
}

const initialKanbanFilters: KanbanFiltersState = {
  query: '',
  companyFilter: '',
  sortBy: 'dateDesc',
  hideInactive: false,
};

const SORT_KEYS: ReadonlySet<string> = new Set<JobSortKey>(['dateDesc', 'dateAsc', 'titleAsc', 'companyAsc']);

/** Drag-to-reorder order per kanban column: a list of group ids (see group-applied-jobs-for-kanban's
 * `id`) in the order the user dropped them. Cleared whenever the sort dropdown changes, so it only
 * ever overrides the active sort until the user explicitly picks a different one. */
type KanbanManualOrderState = Partial<Record<BoardColumnKey, string[]>>;

function sanitizeManualOrder(value: unknown): KanbanManualOrderState {
  if (!value || typeof value !== 'object') return {};
  const out: KanbanManualOrderState = {};
  for (const [column, order] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(order) && order.every((id) => typeof id === 'string')) {
      out[column as BoardColumnKey] = order as string[];
    }
  }
  return out;
}

interface UIState {
  hasSeenSearchPulse: boolean;
  /** Per-column collapse override for the My Jobs kanban; absent columns fall back to {@link isKanbanColumnCollapsedByDefault}. */
  collapsedKanbanColumns: Partial<Record<BoardColumnKey, boolean>>;
  /** Hub Activity widget (Sankey funnel) open/closed — was a raw document cookie, moved into persisted state. */
  activityChartOpen: boolean;
  /** My Jobs kanban search/company/sort/hide-inactive controls. */
  kanbanFilters: KanbanFiltersState;
  /** My Jobs kanban drag-to-reorder overrides, per column. */
  kanbanManualOrder: KanbanManualOrderState;
}

const initialState: UIState = {
  hasSeenSearchPulse: false,
  collapsedKanbanColumns: {},
  activityChartOpen: true,
  kanbanFilters: initialKanbanFilters,
  kanbanManualOrder: {},
};

/** Terminal columns (Hired/Rejected) default to a collapsed rail since they're rarely interacted with; Offer stays expanded while it has cards. */
export function isKanbanColumnCollapsedByDefault(column: BoardColumnKey): boolean {
  return column === 'hired' || column === 'rejected';
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setHasSeenSearchPulse(state, action: PayloadAction<boolean>) {
      state.hasSeenSearchPulse = action.payload;
    },
    setKanbanColumnCollapsed(state, action: PayloadAction<{ column: BoardColumnKey; collapsed: boolean }>) {
      state.collapsedKanbanColumns[action.payload.column] = action.payload.collapsed;
    },
    setActivityChartOpen(state, action: PayloadAction<boolean>) {
      state.activityChartOpen = action.payload;
    },
    setKanbanFilters(state, action: PayloadAction<Partial<KanbanFiltersState>>) {
      Object.assign(state.kanbanFilters, action.payload);
      // Picking a sort order is an explicit "go back to sorted" signal — any column the user
      // hand-reordered via drag should stop overriding it.
      if (action.payload.sortBy !== undefined) {
        state.kanbanManualOrder = {};
      }
    },
    setKanbanColumnOrder(state, action: PayloadAction<{ column: BoardColumnKey; order: string[] }>) {
      state.kanbanManualOrder[action.payload.column] = action.payload.order;
    },
    /** Adopts the server's copy of the ui settings (e.g. after a user-state sync conflict) so local state converges instead of re-fighting the server forever. */
    applyServerUi(state, action: PayloadAction<Partial<UIState>>) {
      const server = action.payload;
      if (server.collapsedKanbanColumns) state.collapsedKanbanColumns = server.collapsedKanbanColumns;
      if (typeof server.activityChartOpen === 'boolean') state.activityChartOpen = server.activityChartOpen;
      if (typeof server.hasSeenSearchPulse === 'boolean') state.hasSeenSearchPulse = server.hasSeenSearchPulse;
    },
  },
  extraReducers: (builder) => {
    // Devices that persisted a `ui` slice before collapsedKanbanColumns/activityChartOpen
    // existed get their old, incomplete blob back verbatim on REHYDRATE (redux-persist's
    // default autoMergeLevel1 replaces the whole slice rather than merging fields), which
    // otherwise leaves state.ui.collapsedKanbanColumns undefined and crashes My Jobs.
    builder.addCase(REHYDRATE, (state, action) => {
      const persisted = (action as { payload?: { ui?: Partial<UIState> } }).payload?.ui;
      if (!persisted || typeof persisted !== 'object') return;
      state.collapsedKanbanColumns = persisted.collapsedKanbanColumns ?? initialState.collapsedKanbanColumns;
      state.activityChartOpen =
        typeof persisted.activityChartOpen === 'boolean' ? persisted.activityChartOpen : initialState.activityChartOpen;
      state.hasSeenSearchPulse =
        typeof persisted.hasSeenSearchPulse === 'boolean' ? persisted.hasSeenSearchPulse : initialState.hasSeenSearchPulse;
      const persistedFilters = persisted.kanbanFilters;
      state.kanbanFilters = {
        query: typeof persistedFilters?.query === 'string' ? persistedFilters.query : initialKanbanFilters.query,
        companyFilter:
          typeof persistedFilters?.companyFilter === 'string' ? persistedFilters.companyFilter : initialKanbanFilters.companyFilter,
        sortBy:
          typeof persistedFilters?.sortBy === 'string' && SORT_KEYS.has(persistedFilters.sortBy)
            ? persistedFilters.sortBy
            : initialKanbanFilters.sortBy,
        hideInactive:
          typeof persistedFilters?.hideInactive === 'boolean' ? persistedFilters.hideInactive : initialKanbanFilters.hideInactive,
      };
      state.kanbanManualOrder = sanitizeManualOrder(persisted.kanbanManualOrder);
    });
  },
});

export const {
  setHasSeenSearchPulse,
  setKanbanColumnCollapsed,
  setActivityChartOpen,
  setKanbanFilters,
  setKanbanColumnOrder,
  applyServerUi,
} = uiSlice.actions;
export default uiSlice.reducer;
